import {Box, Flex, Text} from 'exsportia-components'
import React, {FC, useEffect, useMemo, useState} from 'react'

import {useLocale} from '../../../utils/i18n'

import {FieldProps, Icons} from './types'

type FileUploadFieldProps = FieldProps & {
  icons: Icons
  isUploading: boolean
  isReady: boolean
  openUploadWidget: () => void
}

const isImageUrl = (url: string) => /\.(jpe?g|png|gif|webp|svg|bmp)($|\?)/i.test(url) || url.includes('/image/upload/')

export const FileUploadField: FC<FileUploadFieldProps> = ({
  question,
  value,
  theme,
  icons,
  isUploading,
  isReady,
  openUploadWidget
}) => {
  const {l} = useLocale()
  const config = question.config as any

  const [imageError, setImageError] = useState(false)
  useEffect(() => {
    setImageError(false)
  }, [value])

  const isImage = useMemo(() => {
    if (!value || typeof value !== 'string') {
      return false
    }
    return isImageUrl(value)
  }, [value])

  const filename = useMemo(() => {
    if (!value || typeof value !== 'string') {
      return ''
    }
    const parts = value.split('/')
    const name = parts[parts.length - 1].split('?')[0]

    if (name.length > 25) {
      const extMatch = name.match(/\.[^.]+$/)
      const ext = extMatch ? extMatch[0] : ''
      const basename = extMatch ? name.slice(0, -ext.length) : name
      return basename.length > 18 ? `${basename.slice(0, 18)}...${ext}` : name
    }
    return name
  }, [value])

  const handleUploadClick = () => {
    if (isUploading || !isReady) {
      return
    }
    openUploadWidget()
  }

  return (
    <Box>
      <Box
        display='block'
        p='24px'
        border={`2px dashed ${isUploading ? theme.colors.primary : theme.colors.border}`} // Simplified
        borderRadius='12px'
        textAlign='center'
        bg={value ? `${theme.colors.primary}05` : `${theme.colors.bgGray}40`}
        cursor={isUploading || !isReady ? 'not-allowed' : 'pointer'}
        hover={{borderColor: theme.colors.primary, bg: `${theme.colors.primary}05`}}
        transition='all 0.2s'
        onClick={handleUploadClick}
      >
        <Flex flexDirection='column' alignItems='center' gap='8px' pointerEvents='none'>
          {isUploading ? (
            <Box width='32px' height='32px'>
              <Box
                width='100%'
                height='100%'
                borderRadius='50%'
                border={`3px solid ${theme.colors.border}`}
                borderTopColor={theme.colors.primary}
                style={{animation: 'spin 1s linear infinite'}}
              />
              <style>{'@keyframes spin {0% {transform: rotate(0deg);} 100% {transform: rotate(360deg);}}'}</style>
            </Box>
          ) : value ? (
            isImage && typeof value === 'string' && !imageError ? (
              <img
                src={value}
                alt='Uploaded'
                onError={() => setImageError(true)}
                style={{width: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: '8px', marginBottom: '8px'}}
              />
            ) : (
              icons.check({w: '32px', h: '32px', color: '#2ecc71'})
            )
          ) : config?.examplePhoto ? (
            <img
              src={config.examplePhoto}
              alt='Example'
              style={{width: '64px', height: '64px', objectFit: 'cover', borderRadius: '12px', marginBottom: '8px'}}
            />
          ) : (
            icons.upload({w: '32px', h: '32px', color: theme.colors.primary})
          )}

          <Text settings={{fontWeight: '600', color: value && !isImage ? '#2ecc71' : theme.colors.primary}}>
            {isUploading
              ? 'Uploading...'
              : value
                ? isImage && !imageError ? 'Image Uploaded Successfully' : filename
                : config?.documentName || 'Click to Upload File'}
          </Text>

          {value && !isUploading && (
            <Text settings={{fontSize: '11px', color: theme.colors.gray}}>
              Click to replace current file
            </Text>
          )}

          {!value && (
            <Text settings={{fontSize: '12px', color: theme.colors.gray}}>
              {config?.description ? `${config.description} ${l('max10mb')}` : l('maxFileSize10mb')}
            </Text>
          )}
        </Flex>
      </Box>
    </Box>
  )
}
